import type { ErrorItem } from "@auth0/auth0-acul-react/types";

/**
 * Mapeia os códigos de erro que o Auth0 devolve às telas do Universal Login
 * (ACUL) para mensagens claras em pt-BR.
 *
 * Hoje as telas renderizam `error.message` cru — uma string genérica/técnica do
 * Auth0 que confunde o usuário. O campo `error.code` (sempre presente em
 * `ErrorItem`) é o que usamos aqui para escolher uma mensagem clara.
 *
 * Espelha o padrão de `client-view-wallet-saas/src/auth/auth0-errors.ts`
 * (um `switch(code)` retornando pt-BR), mas para os erros que surgem DENTRO das
 * telas do Auth0 (credencial inválida, e-mail já cadastrado, captcha, etc.).
 *
 * Importante: os códigos vêm do servidor Auth0 em runtime e NÃO são enumerados
 * nos tipos do SDK, então o `default` precisa ser robusto — cai em
 * `error.message` (comportamento atual) e só então numa genérica. Assim nenhum
 * código não mapeado regride.
 */

type MappableError = Pick<ErrorItem, "code" | "message"> | null | undefined;

const GENERIC_FALLBACK = "Não foi possível concluir. Tente novamente.";

/** Códigos que indicam que o e-mail/usuário já está cadastrado (cadastro). */
const EMAIL_ALREADY_REGISTERED_CODES = new Set([
  "user_exists",
  "username_exists",
  "invalid_signup",
]);

/** Códigos de falha de credencial no login (e-mail OU senha incorretos). */
const INVALID_CREDENTIALS_CODES = new Set([
  // Código REAL que o tenant ACUL devolve no /u/login/password (verificado em
  // runtime: { code: "wrong-email-credentials", field: "password" }). Sem ele,
  // a desambiguação nunca disparava.
  "wrong-email-credentials",
  "invalid_user_password",
  "wrong_credentials",
  "invalid_credentials",
]);

/**
 * Retorna `true` quando o erro indica que o e-mail já possui conta — usado pela
 * tela de cadastro para exibir, além da mensagem, um link para "Entrar".
 */
export function isEmailAlreadyRegisteredError(error: MappableError): boolean {
  return !!error?.code && EMAIL_ALREADY_REGISTERED_CODES.has(error.code);
}

/** Retorna `true` para falhas de credencial no login. */
export function isInvalidCredentialsError(error: MappableError): boolean {
  return !!error?.code && INVALID_CREDENTIALS_CODES.has(error.code);
}

/**
 * Converte um erro do Auth0 em mensagem pt-BR clara. Sempre retorna uma string.
 */
export function mapAuthError(error: MappableError): string {
  const code = error?.code;

  switch (code) {
    // Credencial inválida no login. No login-password isto é desambiguado em
    // tempo de execução (e-mail inexistente x senha errada); esta é a genérica.
    case "wrong-email-credentials":
    case "invalid_user_password":
    case "wrong_credentials":
    case "invalid_credentials":
      return "E-mail ou senha incorretos.";

    // Rate limit / tentativas em excesso.
    case "too_many_attempts":
    case "too_many_requests":
    case "rate_limit":
      return "Muitas tentativas. Aguarde alguns minutos e tente novamente.";

    // Conta bloqueada por proteção de segurança.
    case "user_blocked":
    case "blocked_user":
    case "user_is_blocked":
      return "Sua conta está temporariamente bloqueada por segurança. Tente novamente mais tarde ou redefina sua senha.";

    // Senha vazada em base de dados conhecida (breached password detection).
    case "password_leaked":
    case "password-leaked":
      return "Esta senha apareceu em vazamentos de dados. Por segurança, redefina sua senha.";

    // E-mail já cadastrado (cadastro).
    case "user_exists":
    case "username_exists":
    case "invalid_signup":
      return "Este e-mail já está cadastrado. Faça login ou redefina sua senha.";

    // Captcha / verificação anti-bot.
    case "invalid_captcha":
    case "auth0-idp-captcha":
      return "Código de verificação inválido. Tente novamente.";

    // Políticas de senha.
    case "password_strength_error":
    case "password_dictionary_error":
    case "password_no_user_info_error":
      return "A senha não atende aos requisitos de segurança.";
    case "password_history_error":
      return "Você já usou esta senha. Escolha uma senha diferente.";

    // Acesso negado (regras / pós-login).
    case "access_denied":
      return "Acesso negado. Verifique suas credenciais e tente novamente.";

    default:
      // Preserva a mensagem do servidor quando houver; senão, genérica.
      return error?.message || GENERIC_FALLBACK;
  }
}
